/**
 * Small controlled form for editing a {@link CardIdentity}. Used by:
 *   - The Intake confirm step when confidence < 0.80 (Requirement 2.4)
 *   - The Capture_Batch reviewer when the operator chooses "Correct"
 *   - The Catalog detail page when reviewer/admin overrides identity
 */

import { CARD_LANGUAGES, DEFAULT_CARD_LANGUAGE } from '@tcvs/shared';
import { Fragment, useEffect, useState } from 'react';
import type { CardIdentity } from './types';

interface IdentityFormProps {
  value: CardIdentity;
  onChange: (next: CardIdentity) => void;
  disabled?: boolean;
  hiddenFields?: readonly IdentityFieldKey[];
  compact?: boolean;
  numberLookup?: {
    busy: boolean;
    onLookup: (number: string) => void;
  };
  /** Non-authoritative capture observation used only to preselect the editor. */
  serialNumberedHint?: boolean;
}

/** Inline form with one input per identity field. */
export function IdentityForm({
  value,
  onChange,
  disabled,
  hiddenFields = [],
  compact = false,
  numberLookup,
  serialNumberedHint = false,
}: IdentityFormProps): JSX.Element {
  const [serialEnabled, setSerialEnabled] = useState(
    value.serialNumbered === true ||
      value.serialTo !== undefined ||
      value.serialNumber !== undefined ||
      serialNumberedHint,
  );
  const hidden = new Set<IdentityFieldKey>(hiddenFields);

  useEffect(() => {
    if (
      value.serialNumbered === true ||
      value.serialTo !== undefined ||
      value.serialNumber !== undefined
    ) {
      setSerialEnabled(true);
    } else if (!serialNumberedHint) {
      setSerialEnabled(false);
    }
  }, [serialNumberedHint, value.serialNumber, value.serialTo]);

  return (
    <div className={`tcvs-identity-form${compact ? ' tcvs-identity-form-compact' : ''}`}>
      {!hidden.has('category') ? (
        <>
          <label htmlFor="category">Category</label>
          <input
            id="category"
            type="text"
            value={value.category}
            disabled={disabled === true}
            onChange={(e) => onChange({ ...value, category: e.target.value })}
          />
        </>
      ) : null}
      {!hidden.has('set') ? (
        <>
          <label htmlFor="set">Set</label>
          <input
            id="set"
            type="text"
            value={value.set}
            disabled={disabled === true}
            onChange={(e) => onChange({ ...value, set: e.target.value })}
          />
        </>
      ) : null}
      <label htmlFor="number">Card #</label>
      <div className="tcvs-identity-number-field">
        <input
          id="number"
          type="text"
          value={value.number}
          disabled={disabled === true}
          onChange={(e) => onChange({ ...value, number: e.target.value })}
        />
        {numberLookup !== undefined ? (
          <button
            type="button"
            className="tcvs-btn-compact"
            disabled={disabled === true || numberLookup.busy || value.number.trim() === ''}
            onClick={() => numberLookup.onLookup(value.number)}
          >
            {numberLookup.busy ? 'Looking up…' : 'Look up'}
          </button>
        ) : null}
      </div>
      <label htmlFor="name">Name</label>
      <input
        id="name"
        type="text"
        value={value.name}
        disabled={disabled === true}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
      />
      {/* Additive listing fields imported from the Service_CSV
          (csv-identity-import). Optional; an absent field is omitted from the
          identity rather than stored as ''. */}
      {OPTIONAL_FIELDS.filter(({ key }) => !hidden.has(key)).map(({ key, label }) => (
        <Fragment key={key}>
          <label htmlFor={key}>{label}</label>
          <input
            id={key}
            type="text"
            value={value[key] ?? ''}
            disabled={disabled === true}
            onChange={(e) => {
              const next = { ...value };
              if (e.target.value === '') delete next[key];
              else next[key] = e.target.value;
              onChange(next);
            }}
          />
        </Fragment>
      ))}
      {!hidden.has('language') ? (
        <>
          <label htmlFor="language">Language</label>
          <select
            id="language"
            value={value.language ?? ''}
            disabled={disabled === true}
            onChange={(event) => {
              const next = { ...value };
              if (event.target.value === '') delete next.language;
              else next.language = event.target.value;
              onChange(next);
            }}
          >
            <option value="">Select language</option>
            {CARD_LANGUAGES.map((language) => (
              <option key={language} value={language}>
                {language}
              </option>
            ))}
          </select>
        </>
      ) : null}
      <label htmlFor="rookie">Rookie</label>
      <input
        id="rookie"
        type="checkbox"
        checked={value.rookie === true}
        disabled={disabled === true}
        onChange={(e) => {
          const next = { ...value };
          if (e.target.checked) next.rookie = true;
          else delete next.rookie;
          onChange(next);
        }}
      />
      <label htmlFor="autograph">Autograph</label>
      <input
        id="autograph"
        type="checkbox"
        checked={value.autograph === true}
        disabled={disabled === true}
        onChange={(event) => {
          const next = { ...value };
          if (event.target.checked) next.autograph = true;
          else delete next.autograph;
          onChange(next);
        }}
      />
      <label htmlFor="memorabilia">Memorabilia</label>
      <input
        id="memorabilia"
        type="checkbox"
        checked={value.memorabilia === true}
        disabled={disabled === true}
        onChange={(event) => {
          const next = { ...value };
          if (event.target.checked) next.memorabilia = true;
          else delete next.memorabilia;
          onChange(next);
        }}
      />
      <label htmlFor="serial-numbered">Serial numbered</label>
      <div className="tcvs-serial-number-editor">
        <input
          id="serial-numbered"
          type="checkbox"
          checked={serialEnabled}
          disabled={disabled === true}
          onChange={(event) => {
            setSerialEnabled(event.target.checked);
            const next = { ...value };
            if (event.target.checked) {
              next.serialNumbered = true;
              onChange(next);
            } else {
              delete next.serialNumbered;
              delete next.serialNumber;
              delete next.serialTo;
              onChange(next);
            }
          }}
        />
        {serialEnabled ? (
          <div className="tcvs-serial-number-row">
            <input
              type="number"
              min={1}
              step={1}
              aria-label="Specific card serial number"
              placeholder="Card #"
              value={
                value.serialNumber !== undefined && value.serialNumber >= 1
                  ? value.serialNumber
                  : ''
              }
              disabled={disabled === true}
              onChange={(event) => {
                const next = { ...value };
                const parsed = Number.parseInt(event.target.value, 10);
                if (event.target.value === '' || Number.isNaN(parsed)) delete next.serialNumber;
                else next.serialNumber = parsed;
                onChange(next);
              }}
            />
            <span>out of</span>
            <input
              type="number"
              min={1}
              step={1}
              aria-label="Serial print run size"
              placeholder="Run size"
              value={value.serialTo ?? ''}
              disabled={disabled === true}
              onChange={(event) => {
                const next = { ...value };
                const parsed = Number.parseInt(event.target.value, 10);
                if (event.target.value === '' || Number.isNaN(parsed)) delete next.serialTo;
                else next.serialTo = parsed;
                onChange(next);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Keys of {@link CardIdentity} whose value is a string. Excludes the numeric
 *  `serialTo` (checklist-reference), which is edited by its own dedicated input
 *  rather than the generic string-field loop. */
type StringIdentityKey = Exclude<
  keyof CardIdentity,
  | 'serialTo'
  | 'serialNumber'
  | 'serialNumbered'
  | 'rookie'
  | 'autograph'
  | 'memorabilia'
  | 'language'
>;
type IdentityFieldKey = keyof CardIdentity;

/** The optional listing fields, in display order (csv-identity-import). */
const OPTIONAL_FIELDS: ReadonlyArray<{ key: StringIdentityKey; label: string }> = [
  { key: 'year', label: 'Year' },
  { key: 'brand', label: 'Brand' },
  { key: 'subset', label: 'Subset' },
  { key: 'attributes', label: 'Attributes' },
  { key: 'team', label: 'Team' },
  { key: 'variation', label: 'Parallel' },
];

export const EMPTY_IDENTITY: CardIdentity = {
  category: '',
  set: '',
  number: '',
  name: '',
  language: DEFAULT_CARD_LANGUAGE,
};
